import { FC, useContext, useMemo } from 'react';
import React from 'react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ErrorPage from '../../../pages/Error/Component';
import isUserAuthorized from '../../../Hooks/isUserAuthorized';
import RdContext from '../../../contexts/RdProvider';
import { RdContextType } from '../../../@types/Rd';
import { InteractionStatus } from '@azure/msal-browser';
import { AuthCompanyValidityBlock } from '../AuthValidationsHelper';
import { useAcquireQueryParamValue } from '../../../Hooks/useAcquireQueryParamValue';
import ApiErrorContext, { ApiErrorContextType } from '../../../contexts/ApiErrorProvider';
import { useFullUrl } from '../../../Hooks/useFixUrl';
import { AdAuthGuardProps, RdAuthGuardProps, RdInternalAuthGuardProps } from '../Guards';
import { AdAuthGuard } from "../AdAuth/Component";
import { withMsal, WithMsalProps } from '@azure/msal-react';

const RdInternalAuthGuard: FC<RdAuthGuardProps> = (props: RdAuthGuardProps) => {
    const { Permissions, Companies, currentCompany, userInfo, RedirectLogin } = useContext<RdContextType>(RdContext),
        { cancelTokenSource } = useContext<ApiErrorContextType>(ApiErrorContext),
        { isAdAuthenticated, AdAuthProgress } = props as RdInternalAuthGuardProps,
        { children, check } = props,
        [requestedLocation, setRequestedLocation] = useState<string | null>(),
        companyParam = useAcquireQueryParamValue("Company"),
        companyId = currentCompany?.Id.toString(),
        companyName = useMemo(() => (AdAuthProgress == InteractionStatus.None && currentCompany?.Name != null && currentCompany?.Name != "" && currentCompany.Name.trim()) || undefined, [companyId, AdAuthProgress]) || "Desconhecida",
        location = useLocation(),
        isLoggedAuthorized: boolean = (check && check.some(role => isUserAuthorized({ isAdAuthenticated, permissions: Permissions, role, companyId, user: userInfo }))) || false,
        hasCompanyParam: boolean = companyParam != null && companyParam != "",
        isAdOk: boolean = AdAuthProgress == InteractionStatus.None && isAdAuthenticated,
        haveAdSignIn: boolean = (AdAuthProgress == InteractionStatus.None && !isAdAuthenticated),
        haveCompanySignIn: boolean = isAdOk && hasCompanyParam && (currentCompany == null && Companies != null && Companies.some(x => x.Id > 0) || (currentCompany != null && currentCompany.Id != Number.parseInt(companyParam))),
        fullUrlVisited: string = useFullUrl();

    if (haveCompanySignIn) {
        RedirectLogin && RedirectLogin(companyParam);
        return <Navigate to={fullUrlVisited} replace={hasCompanyParam} />;
    }

    const companyBlock = AuthCompanyValidityBlock(companyName, isAdAuthenticated, AdAuthProgress);
    if (companyBlock != null)
        return companyBlock;

    if (AdAuthProgress == InteractionStatus.None && (check && !isLoggedAuthorized)) {
        if (fullUrlVisited !== requestedLocation) {
            setRequestedLocation(fullUrlVisited);
        }

        if (companyParam == null || companyParam == "")
            cancelTokenSource?.cancel();

        return <ErrorPage errorCode={401} />
    }

    // This is done so that in case the route changes by any chance through other
    // means between the moment of request and the render we navigate to the initially
    // requested route.
    if (requestedLocation && location.pathname !== requestedLocation) {
        setRequestedLocation(null);

        return <Navigate to={requestedLocation} />;
    }

    return <>{children}</>;
};

const RdAuthGuard = withMsal<AdAuthGuardProps>(props => {
    const AdCheck = AdAuthGuard(props as WithMsalProps);
    return <RdInternalAuthGuard {...props} {...AdCheck} />;
});

export default RdAuthGuard;